import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

interface KPICardProps {
  title: string;
  value: number | string | null;
  change: number | null;
  sparklineData?: Array<{ value: number }>;
  format?: 'currency' | 'percentage' | 'number';
  loading?: boolean;
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  sparklineData,
  format = 'number',
  loading = false,
}) => {
  const formatValue = (val: number | string | null): string => {
    if (val === null || val === undefined) return 'N/A';
    if (typeof val === 'string') return val;
    
    switch (format) {
      case 'currency':
        return `$${val.toFixed(2)}`;
      case 'percentage':
        return `${val.toFixed(1)}%`;
      default:
        return val.toLocaleString();
    }
  };

  const formatChange = (val: number | null): string => {
    if (val === null || val === undefined) return 'N/A';
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}%`;
  };

  const isPositive = change !== null ? change >= 0 : true;

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 140 }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="80%" height={36} sx={{ mt: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={40} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', minHeight: 140 }}>
      <CardContent>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ fontSize: '0.875rem', fontWeight: 500 }}
        >
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mt: 1 }}>
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: 600 }}
          >
            {formatValue(value)}
          </Typography>
          
          {change !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: isPositive ? 'success.main' : 'error.main',
              }}
            >
              {isPositive ? (
                <TrendingUpIcon sx={{ fontSize: 20 }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 20 }} />
              )}
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                {formatChange(change)}
              </Typography>
            </Box>
          )}
        </Box>

        {sparklineData && sparklineData.length > 0 && (
          <Box sx={{ mt: 2, height: 40 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparklineData}>
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={isPositive ? '#4caf50' : '#f44336'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
        
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          vs. last year
        </Typography>
      </CardContent>
    </Card>
  );
};